import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServicesStore } from "../stores/servicesStore";
import { useAuthStore } from "../stores/authStore";
import { queryServicesProtected } from "../lib/servicesApi";
import type { Service } from "../types/database";

export function useServices() {
  const { user, token } = useAuthStore();
  const { setServices } = useServicesStore();

  return useQuery({
    queryKey: ["services", user?.id],
    queryFn: async () => {
      if (!user?.id || !token) return [];

      const services = await queryServicesProtected<Service[]>(token, "list", {});
      setServices(services);
      return services;
    },
    enabled: !!user?.id && !!token,
  });
}

export function useCreateService() {
  const queryClient = useQueryClient();
  const { token } = useAuthStore();
  const { addService } = useServicesStore();

  return useMutation({
    mutationFn: async (serviceData: Omit<Service, "id" | "user_id" | "created_at" | "updated_at" | "operator">) => {
      if (!token) throw new Error("User not authenticated");

      return await queryServicesProtected<Service>(token, "create", serviceData as Record<string, unknown>);
    },
    onSuccess: (data) => {
      addService(data);
      queryClient.invalidateQueries({ queryKey: ["services"] });
    },
  });
}

export function useUpdateService() {
  const queryClient = useQueryClient();
  const { token } = useAuthStore();
  const { updateService } = useServicesStore();

  return useMutation({
    mutationFn: async ({
      id,
      ...updates
    }: Partial<Service> & { id: string }) => {
      if (!token) throw new Error("User not authenticated");

      return await queryServicesProtected<Service>(token, "update", { id, ...updates });
    },
    onSuccess: (data) => {
      updateService(data.id, data);
      queryClient.invalidateQueries({ queryKey: ["services"] });
    },
  });
}

export function useDeleteService() {
  const queryClient = useQueryClient();
  const { token } = useAuthStore();
  const { removeService } = useServicesStore();

  return useMutation({
    mutationFn: async (id: string) => {
      if (!token) throw new Error("User not authenticated");

      await queryServicesProtected<{ id: string }>(token, "delete", { id });
      return id;
    },
    onSuccess: (id) => {
      removeService(id);
      queryClient.invalidateQueries({ queryKey: ["services"] });
    },
  });
}
